import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../../Context/AuthContext";
import { fetchPDashboardData } from "../../Api/projectDashboard";
import "../../Styling/ProjectDashboard.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ProjectDashboard = () => {
  const { user } = useAuth();
  
  // Memoize userOrgId to prevent unnecessary recalculations
  const userOrgId = useMemo(() => 
    user?.organisationId?.toString() || "", 
    [user?.organisationId]
  );

  const [filters, setFilters] = useState({
    companyCode: "",
    fiscalYear: "",
    fiscalMonth: "",
  });
  
  const [companyCodes, setCompanyCodes] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  
  // Remove debugInfo state to reduce re-renders
  const debugInfoRef = useRef({
    apiParams: {},
    responseCount: 0,
    filteredCount: 0
  });

  // Memoize fiscal years
  const fiscalYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: 2026 - currentYear + 1 },
      (_, i) => (currentYear + i).toString()
    );
  }, []);

  // Memoize months
  const months = useMemo(() => [
    { value: "", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ], []);

  // Memoize company codes loader
  const loadCompanyCodes = useCallback((orgId) => {
    if (!orgId) return [];
    
    if (orgId === "9001") {
      return [
        { value: "1000", label: "1000 - Saisatwik Technologies Private Limited" },
        { value: "2000", label: "2000 - Bizflow" }
      ];
    } else if (orgId === "5000") {
      return [
        { value: "5000", label: "5000" }
      ];
    } else {
      return [{ value: orgId, label: `Company ${orgId}` }];
    }
  }, []);

  // Main data loader - memoized and optimized
  const loadData = useCallback(async (filterParams = filters) => {
    if (!userOrgId) {
      setError("User organization ID not found");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Prepare query parameters
      const queryParams = {
        OrganisationID: parseInt(userOrgId),
        ...(filterParams.companyCode && { CompanyCode: parseInt(filterParams.companyCode) }),
        ...(filterParams.fiscalYear && { FiscalYear: parseInt(filterParams.fiscalYear) }),
        ...(filterParams.fiscalMonth && { FiscalMonth: parseInt(filterParams.fiscalMonth) }),
      };

      console.log("API Params:", queryParams); // Debug log
      const response = await fetchPDashboardData(queryParams, user.token);
      
      // Process response data
      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (response?.data) {
        data = Array.isArray(response.data) ? response.data : [response.data];
      }

      console.log("API Response:", data); // Debug log

      // Filter data based on filters
      let filteredData = data;
      
      if (filterParams.companyCode) {
        filteredData = filteredData.filter(item => 
          item.companyCode?.toString() === filterParams.companyCode
        );
      }
      
      if (filterParams.fiscalYear) {
        filteredData = filteredData.filter(item => 
          item.fiscalYear?.toString() === filterParams.fiscalYear
        );
      }
      
      if (filterParams.fiscalMonth) {
        filteredData = filteredData.filter(item => 
          item.fiscalMonth?.toString() === filterParams.fiscalMonth
        );
      }

      debugInfoRef.current = {
        apiParams: queryParams,
        responseCount: data.length,
        filteredCount: filteredData.length
      };

      setGridData(filteredData);
      
    } catch (err) {
      setError("Failed to load dashboard data: " + err.message);
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }, [userOrgId, user?.token]);

  // Load initial data and company codes
  useEffect(() => {
    if (userOrgId) {
      const codes = loadCompanyCodes(userOrgId);
      setCompanyCodes(codes);
      loadData();
    }
  }, [userOrgId, loadCompanyCodes]);

  // Handle filter changes with debouncing
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      companyCode: "",
      fiscalYear: "",
      fiscalMonth: "",
    });
  };

  // Apply filters immediately when changed
  useEffect(() => {
    if (userOrgId) {
      const timer = setTimeout(() => {
        loadData(filters);
      }, 300); // Small delay to batch rapid changes
      
      return () => clearTimeout(timer);
    }
  }, [filters, userOrgId]);

  // Memoize summary calculations
  const summaryData = useMemo(() => {
    const totalPO = gridData.reduce((sum, row) => 
      sum + (parseFloat(row.poAmount) || 0), 0
    );
    
    const uniqueCustomers = new Set(
      gridData.map(row => row.customerCode).filter(Boolean)
    ).size;
    
    return {
      totalProjects: gridData.length,
      totalPOAmount: totalPO,
      activeCustomers: uniqueCustomers,
    };
  }, [gridData]);

  // Memoize currency formatter
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  }, []);

  // Format date for display
  const formatDate = useCallback((date = new Date()) => {
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const handleExportExcel = useCallback(() => {
    if (gridData.length === 0) return;
    
    const headers = [
      'Customer Code', 'Customer Name', 'Project Code', 
      'Project Name', 'PO Amount (₹)'
    ];
    
    const rows = gridData.map(row => [
      row.customerCode || '',
      `"${(row.customerName || '').replace(/"/g, '""')}"`,
      row.projectCode || '',
      `"${(row.projectName || '').replace(/"/g, '""')}"`,
      row.poAmount || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `Project_Dashboard_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [gridData]);

  const handleExportPDF = useCallback(async () => {
    if (gridData.length === 0) return;
    
    try {
      setPdfGenerating(true);
      
      // Create a temporary div to hold the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.style.cssText = `
        position: absolute;
        left: -10000px;
        top: -10000px;
        width: 794px; /* A4 width in pixels */
        padding: 40px;
        background: white;
        font-family: 'Arial', sans-serif;
        color: #333;
      `;
      
      // Build the PDF content
      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      
      // Get selected filters for display
      const selectedCompany = companyCodes.find(c => c.value === filters.companyCode);
      const selectedMonth = months.find(m => m.value === filters.fiscalMonth);
      const selectedYear = filters.fiscalYear;
      
      pdfContent.innerHTML = `
        <div id="pdf-content" style="width: 100%;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px;">
            <h1 style="color: #1e293b; margin: 0 0 10px 0; font-size: 28px;">Project Dashboard Report</h1>
            <div style="display: flex; justify-content: space-between; font-size: 12px; color: #64748b;">
              <div>Generated: ${formatDate(currentDate)}</div>
              <div>Organization ID: ${userOrgId}</div>
            </div>
          </div>
          
          <!-- Summary Section -->
          <div style="margin-bottom: 30px; background: #f8fafc; padding: 20px; border-radius: 8px;">
            <h2 style="color: #1e293b; margin: 0 0 15px 0; font-size: 18px;">Summary</h2>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
              <div style="text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #2563eb;">${summaryData.totalProjects}</div>
                <div style="font-size: 12px; color: #64748b;">Total Projects</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #10b981;">${formatCurrency(summaryData.totalPOAmount)}</div>
                <div style="font-size: 12px; color: #64748b;">Total PO Amount</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #f59e0b;">${summaryData.activeCustomers}</div>
                <div style="font-size: 12px; color: #64748b;">Active Customers</div>
              </div>
            </div>
          </div>
          
          <!-- Filters Applied -->
          <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #e2e8f0; border-radius: 6px;">
            <h3 style="color: #1e293b; margin: 0 0 10px 0; font-size: 14px;">Filters Applied:</h3>
            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; font-size: 12px;">
              <div>
                <strong>Company:</strong><br>
                ${selectedCompany ? selectedCompany.label : 'All Companies'}
              </div>
              <div>
                <strong>Year:</strong><br>
                ${selectedYear || 'All Years'}
              </div>
              <div>
                <strong>Month:</strong><br>
                ${selectedMonth ? selectedMonth.label : 'All Months'}
              </div>
              <div>
                <strong>Records:</strong><br>
                ${gridData.length}
              </div>
            </div>
          </div>
          
          <!-- Data Table -->
          <div style="margin-bottom: 20px;">
            <h2 style="color: #1e293b; margin: 0 0 15px 0; font-size: 18px;">Project Details</h2>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              <thead>
                <tr style="background-color: #f1f5f9;">
                  <th style="padding: 10px; border: 1px solid #e2e8f0; text-align: left; font-weight: bold;">Customer Code</th>
                  <th style="padding: 10px; border: 1px solid #e2e8f0; text-align: left; font-weight: bold;">Customer Name</th>
                  <th style="padding: 10px; border: 1px solid #e2e8f0; text-align: left; font-weight: bold;">Project Code</th>
                  <th style="padding: 10px; border: 1px solid #e2e8f0; text-align: left; font-weight: bold;">Project Name</th>
                  <th style="padding: 10px; border: 1px solid #e2e8f0; text-align: left; font-weight: bold;">PO Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                ${gridData.map((row, index) => `
                  <tr style="${index % 2 === 0 ? 'background-color: #f8fafc;' : ''}">
                    <td style="padding: 8px; border: 1px solid #e2e8f0; vertical-align: top;">${row.customerCode || "-"}</td>
                    <td style="padding: 8px; border: 1px solid #e2e8f0; vertical-align: top;">${row.customerName || "-"}</td>
                    <td style="padding: 8px; border: 1px solid #e2e8f0; vertical-align: top;">${row.projectCode || "-"}</td>
                    <td style="padding: 8px; border: 1px solid #e2e8f0; vertical-align: top;">${row.projectName || "-"}</td>
                    <td style="padding: 8px; border: 1px solid #e2e8f0; vertical-align: top; font-weight: bold; color: #10b981; text-align: right;">
                      ${row.poAmount ? formatCurrency(row.poAmount) : "-"}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <!-- Footer -->
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center;">
            <div>Confidential - For Internal Use Only</div>
            <div>Page 1 of 1 • Generated from Project Dashboard System</div>
          </div>
        </div>
      `;
      
      // Add to document
      document.body.appendChild(pdfContent);
      
      // Generate PDF
      const canvas = await html2canvas(pdfContent, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add new page if content is too long
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save the PDF
      pdf.save(`Project_Dashboard_Report_${currentDate.getFullYear()}_${(currentDate.getMonth() + 1).toString().padStart(2, '0')}_${currentDate.getDate().toString().padStart(2, '0')}.pdf`);
      
      // Clean up
      document.body.removeChild(pdfContent);
      
    } catch (err) {
      console.error('PDF generation error:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  }, [gridData, userOrgId, companyCodes, filters, months, summaryData, formatCurrency, formatDate]);

  // Memoize clean company name function
  const cleanCompanyName = useCallback((name) => {
    return name ? name.replace(/\r\n/g, ' ').replace(/\s+/g, ' ').trim() : '';
  }, []);

  return (
    <div className="pd-container">
      {/* Header Section */}
      <div className="pd-header">
        <div className="pd-header__left">
          <h1 className="pd-header__title">Project Dashboard</h1>
          <p className="pd-header__subtitle">Monitor and analyze project performance metrics</p>
          <div className="pd-user-org">
            <span className="pd-user-org__label">Organization ID:</span>
            <span className="pd-user-org__value">{userOrgId}</span>
          </div>
        </div>
        <div className="pd-header__actions">
          <button 
            className="pd-btn pd-btn--secondary"
            onClick={() => loadData(filters)}
            disabled={loading}
          >
            <span className="pd-btn__icon">↻</span> Refresh
          </button>
          <button 
            className="pd-btn pd-btn--excel"
            onClick={handleExportExcel}
            disabled={gridData.length === 0 || loading}
          >
            <span className="pd-btn__icon">📊</span> Excel
          </button>
          <button 
            className="pd-btn pd-btn--pdf"
            onClick={handleExportPDF}
            disabled={gridData.length === 0 || loading || pdfGenerating}
          >
            <span className="pd-btn__icon">
              {pdfGenerating ? '⏳' : '📄'}
            </span>
            {pdfGenerating ? 'Generating...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Summary Cards - Optimized with memo */}
      <div className="pd-summary-grid">
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#e3f2fd' }}>
            📊
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total Projects</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : summaryData.totalProjects}
            </p>
          </div>
        </div>
        
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#e8f5e9' }}>
            ₹
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Total PO Amount</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : formatCurrency(summaryData.totalPOAmount)}
            </p>
          </div>
        </div>
        
        <div className="pd-summary-card">
          <div className="pd-summary-card__icon" style={{ backgroundColor: '#fff3e0' }}>
            👥
          </div>
          <div className="pd-summary-card__content">
            <h3 className="pd-summary-card__title">Active Customers</h3>
            <p className="pd-summary-card__value">
              {loading ? "..." : summaryData.activeCustomers}
            </p>
          </div>
        </div>
      </div>

      {/* Filters Section - Simplified */}
      <div className="pd-filters-card">
        <div className="pd-filters-card__header">
          <h3 className="pd-filters-card__title">Filters</h3>
          <button 
            className="pd-btn-text"
            onClick={handleResetFilters}
            disabled={loading}
          >
            Clear Filters
          </button>
        </div>
        
        <div className="pd-filters-grid">
          {/* Organization ID Display */}
          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Organization ID</label>
            <input
              type="text"
              className="pd-filter-group__input pd-filter-group__input--readonly"
              value={userOrgId}
              readOnly
            />
          </div>

          {/* Company Code Dropdown */}
          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Company Code</label>
            <select
              className="pd-filter-group__select"
              name="companyCode"
              value={filters.companyCode}
              onChange={handleFilterChange}
              disabled={loading}
            >
              <option value="">All Companies</option>
              {companyCodes.map((code) => (
                <option key={code.value} value={code.value}>
                  {code.label}
                </option>
              ))}
            </select>
          </div>

          {/* Fiscal Year Dropdown */}
          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Fiscal Year</label>
            <select
              className="pd-filter-group__select"
              name="fiscalYear"
              value={filters.fiscalYear}
              onChange={handleFilterChange}
              disabled={loading}
            >
              <option value="">All Years</option>
              {fiscalYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Month Dropdown */}
          <div className="pd-filter-group">
            <label className="pd-filter-group__label">Month</label>
            <select
              className="pd-filter-group__select"
              name="fiscalMonth"
              value={filters.fiscalMonth}
              onChange={handleFilterChange}
              disabled={loading}
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading Indicator */}
      {(loading || pdfGenerating) && (
        <div className="pd-loading-container">
          <div className="pd-loading-bar"></div>
          {pdfGenerating && (
            <div className="pd-pdf-generating">
              <span className="pd-pdf-generating__text">Generating PDF...</span>
            </div>
          )}
        </div>
      )}

      {/* Data Grid - Optimized rendering */}
      {!loading && gridData.length > 0 && (
        <div className="pd-data-grid-container">
          <div className="pd-data-grid__header">
            <h3 className="pd-data-grid__title">Project Details</h3>
            <span className="pd-data-grid__record-count">
              {gridData.length} records
            </span>
          </div>
          
          <div className="pd-table-wrapper">
            <table className="pd-data-grid">
              <thead>
                <tr className="pd-data-grid__header-row">
                  <th className="pd-data-grid__header-cell">Customer Code</th>
                  <th className="pd-data-grid__header-cell">Customer Name</th>
                  <th className="pd-data-grid__header-cell">Project Code</th>
                  <th className="pd-data-grid__header-cell">Project Name</th>
                  <th className="pd-data-grid__header-cell">PO Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {gridData.map((row, index) => (
                  <tr key={`${row.projectCode}-${index}`} className="pd-data-grid__body-row">
                    <td className="pd-data-grid__body-cell">
                      <span className="pd-data-cell pd-data-cell--code">
                        {row.customerCode || "-"}
                      </span>
                    </td>
                    <td className="pd-data-grid__body-cell">
                      <span className="pd-data-cell pd-data-cell--name">
                        {row.customerName || "-"}
                      </span>
                    </td>
                    <td className="pd-data-grid__body-cell">
                      <span className="pd-data-cell pd-data-cell--code">
                        {row.projectCode || "-"}
                      </span>
                    </td>
                    <td className="pd-data-grid__body-cell">
                      <span className="pd-data-cell pd-data-cell--name">
                        {row.projectName || "-"}
                      </span>
                    </td>
                    <td className="pd-data-grid__body-cell">
                      <span className="pd-data-cell pd-data-cell--amount">
                        {row.poAmount ? formatCurrency(row.poAmount) : "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && gridData.length === 0 && (
        <div className="pd-empty-state">
          <div className="pd-empty-state__icon">📊</div>
          <h3 className="pd-empty-state__title">No data available</h3>
          <p className="pd-empty-state__description">
            Try adjusting your filters or check back later
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="pd-error-state">
          <div className="pd-error-state__icon">⚠️</div>
          <h3 className="pd-error-state__title">Error</h3>
          <p className="pd-error-state__description">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ProjectDashboard;